import React, { useState, useRef, useEffect } from "react";
import { useOnlineUsers } from "@/hooks/useOnlineUsers";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Circle, ChevronDown, ChevronUp } from "lucide-react";

const OnlineUsersStickyBar = () => {
  const { data: onlineUsers } = useOnlineUsers();
  const [isExpanded, setIsExpanded] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setIsExpanded(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  if (!onlineUsers?.length) return null;

  const getInitials = (name: string) =>
    name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2);

  return (
    <div
      ref={ref}
      className="fixed bottom-4 right-4 z-50"
    >
      {isExpanded && (
        <div className="mb-2 bg-popover border rounded-lg shadow-lg p-3 w-56 max-h-64 overflow-y-auto">
          <p className="text-xs font-medium text-muted-foreground mb-2">Online gebruikers</p>
          <div className="space-y-2">
            {onlineUsers.map((user) => (
              <div key={user.medewerker_id} className="flex items-center gap-2">
                <div className="relative">
                  <Avatar className="h-7 w-7">
                    {user.avatar_url ? (
                      <AvatarImage src={user.avatar_url} alt={user.username} />
                    ) : null}
                    <AvatarFallback className="text-[10px] bg-primary/10 text-primary">
                      {getInitials(user.username)}
                    </AvatarFallback>
                  </Avatar>
                  <span className="absolute -bottom-0.5 -right-0.5 h-2 w-2 rounded-full bg-green-500 border-2 border-popover" />
                </div>
                <span className="text-sm text-foreground truncate">{user.username}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center gap-2 bg-popover border rounded-full shadow-lg px-3 py-2 hover:bg-accent transition-colors"
      >
        <Circle className="h-2.5 w-2.5 fill-green-500 text-green-500" />
        <div className="flex -space-x-1.5">
          {onlineUsers.slice(0, 4).map((user) => (
            <Avatar key={user.medewerker_id} className="h-6 w-6 border-2 border-popover">
              {user.avatar_url ? (
                <AvatarImage src={user.avatar_url} alt={user.username} />
              ) : null}
              <AvatarFallback className="text-[9px] bg-primary/10 text-primary">
                {getInitials(user.username)}
              </AvatarFallback>
            </Avatar>
          ))}
        </div>
        <span className="text-xs text-muted-foreground">{onlineUsers.length} online</span>
        {isExpanded ? (
          <ChevronDown className="h-3 w-3 text-muted-foreground" />
        ) : (
          <ChevronUp className="h-3 w-3 text-muted-foreground" />
        )}
      </button>
    </div>
  );
};

export default OnlineUsersStickyBar;
